import { ContentDashboardType } from "../../types/admin/dashboard/ContentDashboardType";
import ContentDashboard from "../../types/admin/dashboard/ContentDashboard";
import ContentDashboardGroup from "../../types/admin/dashboard/ContentDashboardGroup";
import ContentDashboardGroupDetails from "../../types/admin/dashboard/ContentDashboardGroupDetails";
import ContentDashboardWidget from "../../types/admin/dashboard/ContentDashboardWidget";
import { ContentDashboardStatus } from "../../types/admin/dashboard/ContentDashboardStatus";
import { apiClient } from "../apiClient";
import CreateOrUpdateGroupDetailDto from "../../types/admin/dashboard/CreateOrUpdateGroupDetailDto";
import UpsertWidgetDto from "../../types/admin/dashboard/UpsertWidgetDto";
import AnalyticsDataPoint from "../../types/admin/dashboard/AnalyticsDataPoint";
import {
  LoadContentDashboardsByTypeResponseDto,
  CreateContentDashboardDto,
  UpdateContentDashboardBrandAssignmentsDto,
  LoadContentDashboardBrandAssignmentsResponseDto,
} from "../../types/admin/dashboard/DashboardLibraryDto";
import ResultStatus from "../../types/dtos/generic/ResultStatus";

class dashboardManagementApi {
  constructor(authToken: string | undefined) {
    this.accessToken = authToken;
  }

  accessToken: string | undefined;

  /** Load manage dashboard data for the specified dashboard ID */
  loadContentDashboardOverview(
    dashboardId: number,
    onSuccess: (response: ContentDashboard) => void,
    onError: (error: any) => void
  ) {
    return apiClient(
      this.accessToken,
      `dashboards/content-dashboard/get-content-dashboard-overview-by-id?dashboardId=${dashboardId}`
    ).then(
      (response) => {
        onSuccess(response.dashboard);
      },
      (error) => {
        onError(error);
      }
    );
  }

  /** Update the sequence order of dashboard groups */
  updateGroupSequence(
    dashboardId: number,
    groups: ContentDashboardGroup[],
    onSuccess: (response: boolean) => void,
    onError: (error: any) => void
  ) {
    return apiClient(
      this.accessToken,
      `dashboards/content-dashboard/update-group-sequence?dashboardId=${dashboardId}`,
      {
        method: "POST",
        body: JSON.stringify(groups),
      }
    ).then(
      (response) => {
        onSuccess(response);
      },
      (error) => {
        onError(error);
      }
    );
  }

  /** Create a new dashboard group */
  createGroup(
    dashboardId: number,
    dashboardType: ContentDashboardType,
    groupData: CreateOrUpdateGroupDetailDto,
    onSuccess: (response: ContentDashboardGroup) => void,
    onError: (error: any) => void
  ) {
    const params = new URLSearchParams();
    params.append("dashboardId", dashboardId.toString());
    params.append("dashboardType", dashboardType);
    const url = `dashboards/content-dashboard/create-group?${params.toString()}`;
    return apiClient(this.accessToken, url, {
      method: "POST",
      body: JSON.stringify(groupData),
    }).then(
      (response) => {
        onSuccess(response);
      },
      (error) => {
        onError(error);
      }
    );
  }

  /** Load group details for editing */
  loadGroupDetails(
    groupId: number,
    onSuccess: (response: ContentDashboardGroupDetails) => void,
    onError: (error: any) => void
  ) {
    return apiClient(
      this.accessToken,
      `dashboards/content-dashboard/get-group-details?groupId=${groupId}`
    ).then(
      (response) => {
        onSuccess(response);
      },
      (error) => {
        onError(error);
      }
    );
  }

  /** Update an existing dashboard group */
  updateGroup(
    groupId: number,
    groupData: CreateOrUpdateGroupDetailDto,
    onSuccess: (response: ContentDashboardGroup) => void,
    onError: (error: any) => void
  ) {
    return apiClient(
      this.accessToken,
      `dashboards/content-dashboard/update-group?groupId=${groupId}`,
      {
        method: "PUT",
        body: JSON.stringify(groupData),
      }
    ).then(
      (response) => {
        onSuccess(response);
      },
      (error) => {
        onError(error);
      }
    );
  }

  /** Delete a dashboard group (soft delete) */
  deleteGroup(
    groupId: number,
    onSuccess: (response: boolean) => void,
    onError: (error: any) => void
  ) {
    return apiClient(
      this.accessToken,
      `dashboards/content-dashboard/delete-group?groupId=${groupId}`,
      {
        method: "DELETE",
      }
    ).then(
      (response) => {
        onSuccess(response);
      },
      (error) => {
        onError(error);
      }
    );
  }

  /** Get analytics data points for the current client */
  getAnalyticsDataPoints(
    onSuccess: (response: AnalyticsDataPoint[]) => void,
    onError: (error: any) => void
  ) {
    return apiClient(
      this.accessToken,
      `dashboards/content-dashboard/get-analytics-data-points`
    ).then(
      (response) => {
        onSuccess(response);
      },
      (error) => {
        onError(error);
      }
    );
  }

  /** Create or update a widget (upsert) */
  upsertWidget(
    widgetData: UpsertWidgetDto,
    onSuccess: (response: ContentDashboardWidget) => void,
    onError: (error: any) => void
  ) {
    const isUpdate = widgetData.id !== undefined;
    const url = isUpdate
      ? `dashboards/content-dashboard/update-widget?widgetId=${widgetData.id}`
      : `dashboards/content-dashboard/create-widget`;
    const method = isUpdate ? "PUT" : "POST";

    return apiClient(this.accessToken, url, {
      method,
      body: JSON.stringify(widgetData),
    }).then(
      (response) => {
        onSuccess(response);
      },
      (error) => {
        onError(error);
      }
    );
  }

  /** Delete a widget (soft delete) */
  deleteWidget(
    widgetId: number,
    onSuccess: (response: boolean) => void,
    onError: (error: any) => void
  ) {
    return apiClient(
      this.accessToken,
      `dashboards/content-dashboard/delete-widget?widgetId=${widgetId}`,
      {
        method: "DELETE",
      }
    ).then(
      (response) => {
        onSuccess(response);
      },
      (error) => {
        onError(error);
      }
    );
  }

  /** Update dashboard status (Draft/Active). When clientUsesBrands is false, backend may deactivate other dashboards of this type. */
  updateDashboardStatus(
    dashboardId: number,
    dashboardType: ContentDashboardType,
    status: ContentDashboardStatus,
    clientUsesBrands: boolean,
    onSuccess: (response: boolean) => void,
    onError: (error: any) => void
  ) {
    return apiClient(
      this.accessToken,
      `dashboards/content-dashboard/update-dashboard-status?dashboardId=${dashboardId}&dashboardType=${dashboardType}&status=${status}&clientUsesBrands=${clientUsesBrands}`,
      {
        method: "POST",
      }
    ).then(
      (response) => {
        onSuccess(response);
      },
      (error) => {
        onError(error);
      }
    );
  }

  loadContentDashboardsByType(
    dashboardType: ContentDashboardType,
    onSuccess: (response: LoadContentDashboardsByTypeResponseDto) => void,
    onError: (error: any) => void
  ) {
    return apiClient(
      this.accessToken,
      `dashboards/content-dashboard/get-content-dashboards-for-client-by-dashboard-type?dashboardType=${dashboardType}`
    ).then(
      (response) => {
        onSuccess(response);
      },
      (error) => {
        onError(error);
      }
    );
  }

  createContentDashboard(
    dashboardData: CreateContentDashboardDto,
    onSuccess: (dashboardId: number) => void,
    onError: (error: any) => void
  ) {
    return apiClient(
      this.accessToken,
      `dashboards/content-dashboard/create-dashboard`,
      {
        method: "POST",
        body: JSON.stringify(dashboardData),
      }
    ).then(
      (response: ResultStatus) => {
        if (response.success && response.data) {
          onSuccess(response.data as number);
        } else {
          onError(response.message || "Failed to create dashboard");
        }
      },
      (error) => {
        onError(error);
      }
    );
  }

  loadContentDashboardBrandAssignments(
    dashboardId: number,
    onSuccess: (response: LoadContentDashboardBrandAssignmentsResponseDto) => void,
    onError: (error: any) => void
  ) {
    return apiClient(
      this.accessToken,
      `dashboards/content-dashboard/get-brand-assignments-for-dashboard-id?dashboardId=${dashboardId}`
    ).then(
      (response) => {
        onSuccess(response);
      },
      (error) => {
        onError(error);
      }
    );
  }

  updateContentDashboardBrandAssignments(
    data: UpdateContentDashboardBrandAssignmentsDto,
    onSuccess: (response: boolean) => void,
    onError: (error: any) => void
  ) {
    return apiClient(
      this.accessToken,
      `dashboards/content-dashboard/update-brand-assignments`,
      {
        method: "POST",
        body: JSON.stringify(data),
      }
    ).then(
      (response) => {
        onSuccess(response);
      },
      (error) => {
        onError(error);
      }
    );
  }

  deleteContentDashboard(
    dashboardId: number,
    onSuccess: () => void,
    onError: (message: string) => void
  ) {
    return apiClient(
      this.accessToken,
      `dashboards/content-dashboard/delete-dashboard?dashboardId=${dashboardId}`,
      {
        method: "DELETE",
      }
    ).then(
      (response: ResultStatus) => {
        if (response.success) {
          onSuccess();
        } else {
          onError(response.message);
        }
      },
      (error) => {
        onError("An unexpected error occurred.");
      }
    );
  }

  renameContentDashboard(
    dashboardId: number,
    newName: string,
    onSuccess: (response: boolean) => void,
    onError: (error: any) => void
  ) {
    return apiClient(
      this.accessToken,
      `dashboards/content-dashboard/rename-dashboard`,
      {
        method: "POST",
        body: JSON.stringify({ dashboardId, newName }),
      }
    ).then(
      (response) => {
        onSuccess(response);
      },
      (error) => {
        onError(error);
      }
    );
  }
}

export default dashboardManagementApi;
